import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import EditorShell from "./EditorShell";
import { Save, Copy } from "lucide-react";
import TagsInput from "./TagsInput";

export default function PlainTextEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [body, setBody] = useState(initial?.body || "");
  const [tags, setTags] = useState(initial?.tags || []);
  const [busy, setBusy] = useState(false);

  const persist = async ({ asNew }) => {
    if (!name.trim()) return toast.error("Template name required");
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    setBusy(true);
    try {
      const payload = { name: asNew ? `${name} (copy)` : name, subject, body, tags };
      let saved;
      if (initial?.id && !asNew) {
        const { data } = await api.put(`/templates/${initial.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/templates", { ...payload, type: "plain", html: "" });
        saved = data;
      }
      toast.success(asNew ? "Saved as new template" : "Template saved");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <EditorShell
      testid="plain-text-editor"
      title={initial?.id ? "Edit Plain Text Template" : "New Plain Text Template"}
      subtitle="// editor / plain_text"
      onClose={onClose}
      footer={
        <>
          {initial?.id && (
            <button
              type="button"
              onClick={() => persist({ asNew: true })}
              disabled={busy}
              data-testid="plain-text-editor-save-as-new"
              className="cf-btn-secondary"
            >
              <Copy size={14} /> Save as new
            </button>
          )}
          <button
            type="button"
            onClick={() => persist({ asNew: false })}
            disabled={busy}
            data-testid="plain-text-editor-save"
            className="cf-btn-primary"
          >
            <Save size={14} /> {busy ? "Saving..." : "Save Template"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Template name
          </label>
          <input
            data-testid="plain-text-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="cf-input"
            placeholder="e.g. Cold outreach — initial"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Subject
          </label>
          <input
            data-testid="plain-text-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="cf-input"
            placeholder="Quick question, {{name}}"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Tags / folders (press Enter to add)
          </label>
          <TagsInput value={tags} onChange={setTags} testid="plain-text-tags-input" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Body — supports {"{{name}}"} {"{{company}}"}
          </label>
          <textarea
            data-testid="plain-text-body-input"
            rows={14}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="cf-input"
            placeholder="Hi {{name}}, ..."
          />
        </div>
      </div>
    </EditorShell>
  );
}
